import { Component, useMemo, useState, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  correlationId: string | null;
  timestamp: string | null;
}

function generateCorrelationId(): string {
  const cryptoObj =
    typeof globalThis !== 'undefined' && 'crypto' in globalThis
      ? (globalThis as { crypto?: Crypto }).crypto
      : undefined;

  if (cryptoObj?.randomUUID) {
    try {
      return cryptoObj.randomUUID();
    } catch {
      // fall through
    }
  }

  if (cryptoObj?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoObj.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex: string[] = [];
    for (let i = 0; i < bytes.length; i++) {
      hex.push(bytes[i].toString(16).padStart(2, '0'));
    }
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
      .slice(6, 8)
      .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }

  const rand = () => Math.random().toString(16).slice(2, 10).padStart(8, '0');
  return `${rand()}-${rand().slice(0, 4)}-4${rand().slice(0, 3)}-${rand().slice(0, 4)}-${rand()}${rand().slice(0, 4)}`;
}

function safeUserAgent(): string {
  try {
    return typeof navigator !== 'undefined' ? navigator.userAgent : '(userAgent indisponible)';
  } catch {
    return '(userAgent indisponible)';
  }
}

function safeUrl(): string {
  try {
    return typeof window !== 'undefined' ? window.location.href : '(url indisponible)';
  } catch {
    return '(url indisponible)';
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  correlationId: string;
  timestamp: string;
  onRetry: () => void;
}

function ErrorFallback({ error, correlationId, timestamp, onRetry }: ErrorFallbackProps) {
  const [copied, setCopied] = useState(false);

  const diagnosticText = useMemo(
    () =>
      [
        `Timestamp : ${timestamp}`,
        `Correlation ID : ${correlationId}`,
        `User agent : ${safeUserAgent()}`,
        `URL : ${safeUrl()}`,
        `Message : ${error?.message ?? 'Erreur inconnue'}`,
        'Stack :',
        error?.stack ?? '(pas de stack trace)',
      ].join('\n'),
    [error, correlationId, timestamp],
  );

  const copyDiagnostic = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(diagnosticText);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
        return;
      }
      throw new Error('clipboard-api-unavailable');
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = diagnosticText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
        } catch {
          // ignore
        }
        document.body.removeChild(textarea);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        setCopied(false);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-page p-4 dark:bg-slate-900">
      <div className="bg-white rounded-xl shadow-sm p-6 max-w-md w-full dark:bg-slate-800">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-950/40">
            <AlertTriangle className="h-5 w-5 text-brand-500" aria-hidden />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Une erreur est survenue
            </h1>
            <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">
              Nous avons enregistré l&apos;incident. Copiez les infos de debug ci-dessous si vous
              contactez le support.
            </p>
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          <dt className="font-medium text-slate-500 dark:text-slate-400">Correlation ID</dt>
          <dd className="font-mono text-slate-700 break-all dark:text-slate-200">{correlationId}</dd>
          <dt className="font-medium text-slate-500 dark:text-slate-400">Timestamp</dt>
          <dd className="font-mono text-slate-700 break-all dark:text-slate-200">{timestamp}</dd>
        </dl>

        <details className="mt-4 text-xs text-slate-500 dark:text-slate-400">
          <summary className="cursor-pointer font-medium">Détails techniques</summary>
          <pre className="mt-2 bg-slate-100 p-2 rounded text-xs overflow-auto whitespace-pre-wrap dark:bg-slate-900 dark:text-slate-300">
            {diagnosticText}
          </pre>
        </details>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={copyDiagnostic}
            className="px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm touch-manipulation dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
          >
            Copier les infos de debug
          </button>
          <button
            type="button"
            onClick={onRetry}
            className="px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm touch-manipulation dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
          >
            Réessayer
          </button>
          <button
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined') window.location.reload();
            }}
            className="px-3 py-2 rounded-md bg-brand-500 hover:bg-brand-600 text-white text-sm touch-manipulation"
          >
            Recharger la page
          </button>
        </div>

        {copied && (
          <p className="text-xs text-green-600 mt-2 dark:text-green-400">
            Copié dans le presse-papier
          </p>
        )}
      </div>
    </div>
  );
}

export class GsErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, correlationId: null, timestamp: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      correlationId: generateCorrelationId(),
      timestamp: new Date().toISOString(),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const correlationId = this.state.correlationId ?? generateCorrelationId();
    const timestamp = this.state.timestamp ?? new Date().toISOString();

    if (!this.state.correlationId || !this.state.timestamp) {
      this.setState({ correlationId, timestamp });
    }

    console.error('[GsErrorBoundary]', {
      correlationId,
      timestamp,
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });

    this.props.onError?.(error, info);

    // Import dynamique — @gs/telemetry est optionnel, on échoue silencieusement si absent
    void import('@gs/telemetry' as string)
      .then((mod) => {
        const trackError = (mod as { trackError?: unknown })?.trackError;
        if (typeof trackError === 'function') {
          try {
            (trackError as (err: Error, ctx: Record<string, unknown>) => void)(error, {
              correlationId,
              timestamp,
              componentStack: info.componentStack,
              url: safeUrl(),
              userAgent: safeUserAgent(),
            });
          } catch {
            // telemetry échoue en silence, on ne doit jamais masquer l'erreur originale
          }
        }
      })
      .catch(() => {
        // @gs/telemetry non disponible — pas de télémétrie, pas grave
      });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, correlationId: null, timestamp: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    const correlationId = this.state.correlationId ?? generateCorrelationId();
    const timestamp = this.state.timestamp ?? new Date().toISOString();

    return (
      <ErrorFallback
        error={this.state.error}
        correlationId={correlationId}
        timestamp={timestamp}
        onRetry={this.handleRetry}
      />
    );
  }
}
